/**
 * Calculates a comparable version number from semantic version + date string
 * Format: "2.0.0-02.23.2026" where:
 *   - 2.0.0 is semantic version (MAJOR.MINOR.PATCH)
 *   - 02.23.2026 is date (MM.DD.YYYY)
 *
 * Returns: comparable integer where version takes precedence
 * Example: "2.0.0-02.23.2026" -> 200 prefix + date in milliseconds
 */

export interface VersionCalculation {
  versionNumber: number;
  versionInt: number;
  dateMs: number;
}

/**
 * Parse semantic version string to integer
 * "2.0.0" -> 200 (MAJOR * 100 + MINOR * 10 + PATCH)
 * Handles invalid input gracefully
 */
export function parseSemanticVersion(versionString: string | undefined): number {
  if (!versionString || typeof versionString !== 'string') {
    return 0;
  }

  const parts = versionString.split('.');
  const major = parseInt(parts[0] || '0', 10);
  const minor = parseInt(parts[1] || '0', 10);
  const patch = parseInt(parts[2] || '0', 10);

  return major * 100 + minor * 10 + patch;
}

/**
 * Parse date string in MM.DD.YYYY format to milliseconds
 * "02.23.2026" -> milliseconds since epoch
 * Returns 0 if date string is invalid or undefined
 */
export function parseDateToMs(dateString: string | undefined): number {
  if (!dateString) {
    return 0;
  }

  const parts = dateString.split('.');
  if (parts.length < 3) {
    return 0;
  }

  const month = parseInt(parts[0] || '1', 10);
  const day = parseInt(parts[1] || '1', 10);
  const year = parseInt(parts[2] || new Date().getFullYear().toString(), 10);

  const date = new Date(year, month - 1, day); // month is 0-indexed in JS
  return date.getTime();
}

/**
 * Main calculator: converts full version string to comparable number
 * Returns both the combined number and individual components
 * Handles both formats: "2.0.0-02.23.2026" and "2.0.0"
 */
export function calculateVersionNumber(
  versionString: string,
): VersionCalculation {
  if (!versionString || typeof versionString !== 'string') {
    return {
      versionNumber: 0,
      versionInt: 0,
      dateMs: 0,
    };
  }

  // Split version string into semantic version and date parts
  // Format: "2.0.0-02.23.2026" or just "2.0.0"
  const parts = versionString.split('-');
  const semanticVersion = parts[0] || '0.0.0';
  const dateStr = parts[1]; // May be undefined

  const versionInt = parseSemanticVersion(semanticVersion);
  const dateMs = parseDateToMs(dateStr);

  // Combine as: version number takes high precedence, then date
  // Format: "200" + dateMs as single number for easy comparison
  const versionNumber = dateMs > 0 ? parseInt(`${versionInt}${dateMs}`) : versionInt;

  return {
    versionNumber,
    versionInt,
    dateMs,
  };
}

/**
 * Compare two version strings
 * Returns: 1 if versionA > versionB, -1 if versionA < versionB, 0 if equal
 */
export function compareVersions(versionA: string, versionB: string): number {
  const calcA = calculateVersionNumber(versionA);
  const calcB = calculateVersionNumber(versionB);

  if (calcA.versionNumber > calcB.versionNumber) return 1;
  if (calcA.versionNumber < calcB.versionNumber) return -1;
  return 0;
}

/**
 * Check if versionA >= versionB
 */
export function isVersionGreaterOrEqual(
  versionA: string,
  versionB: string,
): boolean {
  return compareVersions(versionA, versionB) >= 0;
}

/**
 * Check if versionA < versionB (update required)
 */
export function isUpdateRequired(
  currentVersion: string,
  requiredVersion: string,
): boolean {
  return compareVersions(currentVersion, requiredVersion) < 0;
}
